import { useState, useEffect } from 'react';
import api from '../api/client';

function TreeNode({ node, selectedId, onSelect }) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = node.id === selectedId;

  return (
    <li>
      <div onClick={() => onSelect(node.id)} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px',
        borderRadius: 8, cursor: 'pointer', fontSize: 13,
        background: isSelected ? '#dbeafe' : 'transparent',
        color: isSelected ? '#2563eb' : '#333',
      }}>
        {hasChildren ? (
          <span style={{ fontSize: 10, color: '#94a3b8', cursor: 'pointer' }} onClick={e => { e.stopPropagation(); setExpanded(!expanded); }}>
            {expanded ? '▼' : '▶'}
          </span>
        ) : <span style={{ width: 10 }} />}
        <span>{hasChildren ? '📁' : '👥'}</span>
        <strong>{node.name}</strong>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>
          ({node.uid}
          {node.leader_name ? ` · ${node.leader_position || ''}: ${node.leader_name}` : ''}
          {parseInt(node.member_count) > 0 ? ` · ${node.member_count}명` : ''})
        </span>
      </div>
      {hasChildren && expanded && (
        <ul style={{ listStyle: 'none', paddingLeft: 24 }}>
          {node.children.map(child => (
            <TreeNode key={child.id} node={child} selectedId={selectedId} onSelect={onSelect} />
          ))}
        </ul>
      )}
    </li>
  );
}

const groupBadge = { '시스템관리자': 'badge-red', '영업관리자': 'badge-green', '영업담당자': 'badge-blue', '가격관리자': 'badge-orange', '뷰어': 'badge-gray' };

export default function OrganizationPage() {
  const [tree, setTree] = useState([]);
  const [flatOrgs, setFlatOrgs] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showMove, setShowMove] = useState(null); // user object
  const [showAssign, setShowAssign] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  // UID
  const [showUid, setShowUid] = useState(false);
  const [orgUidData, setOrgUidData] = useState({ own_uid: '', delegated_uids: [] });
  const [availableOrgUids, setAvailableOrgUids] = useState([]);
  const [selectedUid, setSelectedUid] = useState('');

  // Form
  const [orgForm, setOrgForm] = useState({ name: '', parent_id: '' });
  const [moveTargetOrg, setMoveTargetOrg] = useState('');
  const [assignUserId, setAssignUserId] = useState('');
  const [leaderId, setLeaderId] = useState('');
  const [error, setError] = useState('');
  const [marginInput, setMarginInput] = useState('');
  const [savingMargin, setSavingMargin] = useState(false);

  useEffect(() => { loadTree(); loadAllUsers(); }, []);

  const loadTree = async () => {
    try {
      const data = await api.get('/organizations/tree');
      setTree(data);
      setFlatOrgs(flatten(data));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadAllUsers = async () => {
    try { setAllUsers(await api.get('/users')); } catch (err) { console.error(err); }
  };

  function flatten(nodes, result = []) {
    nodes.forEach(n => { result.push({ id: n.id, name: n.name, uid: n.uid }); if (n.children) flatten(n.children, result); });
    return result;
  }

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId]);

  const loadDetail = async (id) => {
    try {
      const d = await api.get(`/organizations/${id}`);
      setDetail(d);
      setMarginInput(d.min_margin_rate != null ? String(d.min_margin_rate) : '');
    } catch (err) { console.error(err); }
  };

  const saveMarginRate = async () => {
    if (!detail) return;
    setSavingMargin(true);
    try {
      const body = { min_margin_rate: marginInput.trim() === '' ? null : Number(marginInput) };
      const updated = await api.put(`/organizations/${detail.id}/min-margin-rate`, body);
      setDetail(d => ({ ...d, min_margin_rate: updated.min_margin_rate }));
      loadTree();
    } catch (e) { alert(e.message); }
    finally { setSavingMargin(false); }
  };

  // UID 관리
  const openUidModal = async () => {
    setShowUid(true); setSelectedUid('');
    try {
      const [uidData, available] = await Promise.all([
        api.get(`/uid-delegations/by-org/${selectedId}`),
        api.get('/uid-delegations/available-uids?type=department'),
      ]);
      setOrgUidData(uidData);
      setAvailableOrgUids(available);
    } catch (e) { console.error(e); }
  };

  const addOrgUid = async () => {
    if (!selectedUid) return;
    const uidInfo = availableOrgUids.find(u => u.uid === selectedUid);
    try {
      await api.post('/uid-delegations', {
        target_uid: selectedUid, uid_type: 'department',
        original_owner: uidInfo?.owner_name || '', delegate_type: 'org', delegate_org_id: selectedId,
      });
      setSelectedUid(''); openUidModal();
    } catch (e) { alert(e.message); }
  };

  const removeOrgUid = async (delegationId) => {
    try { await api.del(`/uid-delegations/${delegationId}`); openUidModal(); }
    catch (e) { alert(e.message); }
  };

  // 조직 추가
  const handleAdd = async () => {
    setError('');
    if (!orgForm.name) { setError('조직명은 필수입니다.'); return; }
    try {
      await api.post('/organizations', { name: orgForm.name, parent_id: orgForm.parent_id ? parseInt(orgForm.parent_id) : null });
      setShowAdd(false); setOrgForm({ name: '', parent_id: '' }); loadTree();
    } catch (err) { setError(err.message); }
  };

  // 조직 편집
  const openEdit = () => {
    if (!detail) return;
    setOrgForm({ name: detail.name, parent_id: detail.parent_id ? String(detail.parent_id) : '' });
    setLeaderId(detail.leader_user_id ? String(detail.leader_user_id) : '');
    setShowEdit(true); setError('');
  };

  const handleEdit = async () => {
    setError('');
    if (!orgForm.name) { setError('조직명은 필수입니다.'); return; }
    try {
      const body = { name: orgForm.name };
      if (orgForm.parent_id) body.parent_id = parseInt(orgForm.parent_id);
      if (leaderId) body.leader_user_id = parseInt(leaderId);
      await api.put(`/organizations/${selectedId}`, body);
      setShowEdit(false); loadTree(); loadDetail(selectedId);
    } catch (err) { setError(err.message); }
  };

  // 조직 삭제
  const handleDelete = async () => {
    try {
      await api.del(`/organizations/${selectedId}`);
      setShowDelete(false); setSelectedId(null); setDetail(null); loadTree();
    } catch (err) { alert(err.message); setShowDelete(false); }
  };

  // 사용자 조직 이동
  const handleMove = async () => {
    if (!moveTargetOrg) return;
    try {
      await api.post('/organizations/move-user', { user_id: showMove.id, new_org_id: parseInt(moveTargetOrg) });
      setShowMove(null); setMoveTargetOrg(''); loadDetail(selectedId); loadTree(); loadAllUsers();
    } catch (err) { alert(err.message); }
  };

  // 사용자 배정
  const handleAssign = async () => {
    if (!assignUserId) return;
    try {
      await api.put(`/users/${assignUserId}`, { organization_id: selectedId });
      setShowAssign(false); setAssignUserId(''); loadDetail(selectedId); loadTree(); loadAllUsers();
    } catch (err) { alert(err.message); }
  };

  // 현재 조직에 배정되지 않은 사용자
  const unassignedUsers = allUsers.filter(u =>
    !detail?.members?.some(m => m.id === u.id)
  );

  return (
    <div>
      <div className="toolbar">
        <div className="card-title">조직도 관리</div>
        <button className="btn btn-primary btn-sm" onClick={() => { setShowAdd(true); setOrgForm({ name: '', parent_id: selectedId ? String(selectedId) : '' }); setError(''); }}>
          ✚ 조직 추가
        </button>
      </div>

      <div className="grid-2">
        {/* Tree */}
        <div className="card">
          <div className="card-title mb-8">조직 트리</div>
          {loading ? <div style={{ padding: 20, textAlign: 'center' }}>로딩 중...</div> : (
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {tree.map(node => <TreeNode key={node.id} node={node} selectedId={selectedId} onSelect={setSelectedId} />)}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="card">
          {detail ? (
            <>
              <div className="card-header">
                <div className="card-title">{detail.name}</div>
                <div className="btn-group">
                  <button className="btn btn-outline btn-sm" onClick={openEdit}>✏ 편집</button>
                  <button className="btn btn-outline btn-sm" onClick={() => { setShowAssign(true); setAssignUserId(''); }}>+ 사용자 배정</button>
                  <button className="btn btn-outline btn-sm" onClick={openUidModal}>+ UID 추가</button>
                  <button className="btn btn-danger btn-sm" onClick={() => setShowDelete(true)}>비활성화</button>
                </div>
              </div>
              <div style={{ fontSize: 13, lineHeight: 2, marginBottom: 16 }}>
                <div><span style={{ color: '#64748b', display: 'inline-block', width: 80 }}>부서 UID</span>{detail.uid}</div>
                {detail.leader_name && <div><span style={{ color: '#64748b', display: 'inline-block', width: 80 }}>리더</span>{detail.leader_name} ({detail.leader_position})</div>}
                <div><span style={{ color: '#64748b', display: 'inline-block', width: 80 }}>인원</span>{detail.members?.length || 0}명</div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ color: '#64748b', display: 'inline-block', width: 80 }}>최저 공헌이익율</span>
                  <input type="number" min="0" max="100" step="0.1" className="form-input"
                    style={{ width:90, padding:'5px 8px' }}
                    placeholder="미설정"
                    value={marginInput}
                    onChange={e=>setMarginInput(e.target.value)} />
                  <span>%</span>
                  <button className="btn btn-outline btn-sm" disabled={savingMargin} onClick={saveMarginRate}>
                    {savingMargin ? '저장 중...' : '저장'}
                  </button>
                  <span style={{ fontSize:11, color:'#94a3b8' }}>
                    소속원 견적 공헌이익율이 이 값 미만이면 승인 필요. 하위 조직 값 ≥ 상위 조직 값
                  </span>
                </div>
              </div>

              {detail.members && detail.members.length > 0 && (
                <>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>소속 사용자</div>
                  <div style={{overflowX:'auto'}}><table>
                    <thead><tr><th>이름</th><th>UID</th><th>직책</th><th>그룹</th><th>작업</th></tr></thead>
                    <tbody>
                      {detail.members.map(m => (
                        <tr key={m.uid}>
                          <td className="fw-bold">{m.name}</td>
                          <td className="text-muted">{m.uid}</td>
                          <td>{m.position || '-'}</td>
                          <td><span className={`badge ${groupBadge[m.group_name] || 'badge-gray'}`}>{m.group_name}</span></td>
                          <td><button className="btn btn-outline btn-sm" onClick={() => { setShowMove(m); setMoveTargetOrg(''); }}>조직이동</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                  <div className="alert alert-info" style={{ marginTop: 14 }}>
                    ℹ 사용자 조직 이동 시 기존 조직 기반 열람 권한이 초기화됩니다. 본인 작성 및 개별 지정 권한은 유지됩니다.
                  </div>
                </>
              )}

              {(!detail.members || detail.members.length === 0) && (
                <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>소속 사용자가 없습니다.</div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>좌측에서 조직을 선택하세요.</div>
          )}
        </div>
      </div>

      {/* ===== ADD ORG MODAL ===== */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 420 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
              <span>조직 추가</span>
              <span style={{ cursor: 'pointer', color: '#94a3b8' }} onClick={() => setShowAdd(false)}>✕</span>
            </div>
            {error && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{error}</div>}
            <div className="form-group">
              <label className="form-label">조직명 *</label>
              <input className="form-input" value={orgForm.name} onChange={e => setOrgForm({ ...orgForm, name: e.target.value })} placeholder="예: 영업3팀" />
            </div>
            <div className="form-group">
              <label className="form-label">상위 조직</label>
              <select className="form-input" value={orgForm.parent_id} onChange={e => setOrgForm({ ...orgForm, parent_id: e.target.value })}>
                <option value="">없음 (최상위)</option>
                {flatOrgs.map(o => <option key={o.id} value={o.id}>{o.name} ({o.uid})</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowAdd(false)}>취소</button>
              <button className="btn btn-primary" onClick={handleAdd}>추가</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== EDIT ORG MODAL ===== */}
      {showEdit && detail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 420 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
              <span>조직 편집 — {detail.name}</span>
              <span style={{ cursor: 'pointer', color: '#94a3b8' }} onClick={() => setShowEdit(false)}>✕</span>
            </div>
            {error && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{error}</div>}
            <div className="form-group">
              <label className="form-label">조직명 *</label>
              <input className="form-input" value={orgForm.name} onChange={e => setOrgForm({ ...orgForm, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">상위 조직</label>
              <select className="form-input" value={orgForm.parent_id} onChange={e => setOrgForm({ ...orgForm, parent_id: e.target.value })}>
                <option value="">없음 (최상위)</option>
                {flatOrgs.filter(o => o.id !== selectedId).map(o => <option key={o.id} value={o.id}>{o.name} ({o.uid})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">리더</label>
              <select className="form-input" value={leaderId} onChange={e => setLeaderId(e.target.value)}>
                <option value="">없음</option>
                {(detail.members || []).map(m => <option key={m.id} value={m.id}>{m.name} ({m.position || '-'})</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowEdit(false)}>취소</button>
              <button className="btn btn-primary" onClick={handleEdit}>저장</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== DELETE CONFIRM ===== */}
      {showDelete && detail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 400 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid #e2e8f0' }}>조직 비활성화</div>
            <div style={{ fontSize: 13, marginBottom: 16, color: '#475569' }}>
              <strong>"{detail.name}"</strong> 조직을 비활성화하시겠습니까?
            </div>
            <div className="alert alert-warning" style={{ marginBottom: 16 }}>
              ⚠ 조직이 비활성화됩니다. 소속 사용자는 무소속으로 전환되며, 비활성 조직의 UID는 다른 조직에 위임할 수 있습니다. 활성 하위 조직이 있으면 비활성화할 수 없습니다.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowDelete(false)}>취소</button>
              <button className="btn btn-danger" style={{ background: '#ef4444', color: '#fff', border: 'none' }} onClick={handleDelete}>비활성화</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MOVE USER MODAL ===== */}
      {showMove && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 420 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
              <span>조직 이동 — {showMove.name}</span>
              <span style={{ cursor: 'pointer', color: '#94a3b8' }} onClick={() => setShowMove(null)}>✕</span>
            </div>
            <div style={{ fontSize: 13, marginBottom: 12, color: '#475569' }}>
              현재 소속: <strong>{detail?.name}</strong>
            </div>
            <div className="form-group">
              <label className="form-label">이동할 조직 *</label>
              <select className="form-input" value={moveTargetOrg} onChange={e => setMoveTargetOrg(e.target.value)}>
                <option value="">선택</option>
                {flatOrgs.filter(o => o.id !== selectedId).map(o => <option key={o.id} value={o.id}>{o.name} ({o.uid})</option>)}
              </select>
            </div>
            <div className="alert alert-warning" style={{ marginBottom: 16 }}>
              ⚠ 이동 시 기존 조직 기반 견적서 열람 권한이 초기화됩니다. 본인 작성 및 개별 지정 권한은 유지됩니다.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowMove(null)}>취소</button>
              <button className="btn btn-primary" disabled={!moveTargetOrg} onClick={handleMove}>이동</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== ASSIGN USER MODAL ===== */}
      {showAssign && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 460 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
              <span>사용자 배정 — {detail?.name}</span>
              <span style={{ cursor: 'pointer', color: '#94a3b8' }} onClick={() => setShowAssign(false)}>✕</span>
            </div>
            <div className="form-group">
              <label className="form-label">배정할 사용자 *</label>
              <select className="form-input" value={assignUserId} onChange={e => setAssignUserId(e.target.value)}>
                <option value="">선택</option>
                {unassignedUsers.map(u => (
                  <option key={u.uid} value={u.id}>{u.name} ({u.uid}) — {u.org_name || '미배정'}</option>
                ))}
              </select>
            </div>
            {assignUserId && (() => {
              const u = allUsers.find(x => String(x.id) === assignUserId);
              return u?.org_name ? (
                <div className="alert alert-warning" style={{ marginBottom: 12 }}>
                  ⚠ 이 사용자는 현재 <strong>{u.org_name}</strong>에 소속되어 있습니다. 배정 시 기존 조직에서 이동됩니다.
                </div>
              ) : null;
            })()}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowAssign(false)}>취소</button>
              <button className="btn btn-primary" disabled={!assignUserId} onClick={handleAssign}>배정</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== UID MODAL ===== */}
      {showUid && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 520 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
              <span>부서 UID 관리 — {detail?.name}</span>
              <span style={{ cursor: 'pointer', color: '#94a3b8' }} onClick={() => setShowUid(false)}>✕</span>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                <strong>자체 UID:</strong> <span className="badge badge-teal">{orgUidData.own_uid}</span> ({orgUidData.own_name})
              </div>
            </div>

            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>추가 위임된 부서 UID</div>
            {orgUidData.delegated_uids.length > 0 ? (
              <div style={{overflowX:'auto',marginBottom:16}}><table>
                <thead><tr><th>UID</th><th>원래 소유</th><th>위임일</th><th></th></tr></thead>
                <tbody>
                  {orgUidData.delegated_uids.map(d => (
                    <tr key={d.id}>
                      <td><span className="badge badge-teal">{d.target_uid}</span></td>
                      <td>{d.original_owner}</td>
                      <td>{d.created_at?.slice(0, 10)}</td>
                      <td><button className="btn btn-danger btn-sm" onClick={() => removeOrgUid(d.id)}>삭제</button></td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            ) : (
              <div className="text-muted" style={{ marginBottom: 16 }}>추가 위임된 부서 UID가 없습니다.</div>
            )}

            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>부서 UID 추가</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <select className="form-input" value={selectedUid} onChange={e => setSelectedUid(e.target.value)} style={{ flex: 1 }}>
                  <option value="">UID 선택...</option>
                  {availableOrgUids
                    .filter(u => u.uid !== orgUidData.own_uid && !orgUidData.delegated_uids.some(d => d.target_uid === u.uid))
                    .map(u => (
                      <option key={u.uid} value={u.uid}>{u.uid} — {u.owner_name}{u.status === 'inactive' ? ' (비활성)' : ''}</option>
                    ))}
                </select>
                <button className="btn btn-primary btn-sm" disabled={!selectedUid} onClick={addOrgUid}>추가</button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-outline" onClick={() => setShowUid(false)}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
